import { API, graphqlOperation } from "aws-amplify";

import { getInboundShipmentGatePlacement } from "../../../graphql/inbound/custom-queries";
import { getOutboundShipmentGatePlacement } from "../../../graphql/outbound/custom-queries";
import { groupCheckInShipmentIds } from "../../../utils/shipWith";
import {
  gatePlacementRefusal,
  isAlreadyPlaced,
  placeShipment,
} from "../../../utils/shipmentPlacement";

/** The shipment is missing something placement requires. Thrown before the
 * check-in is written, so the driver is not docked either: DOCKED means the
 * load is spotted, and a DOCKED row over an unplaced shipment is a truck the
 * crew Gate Queue can no longer act on (it offers its button to a CALLED
 * driver only). The message is the PlacementRefusal to show the user. */
export class GatePlacementBlockedError extends Error {}

/** How a load is named in a message about a linked truck, where the refusal
 * has to say which of several loads it means. The gate projection carries no
 * PO number today, so the id stands in until it does. */
const loadLabel = (shipment) =>
  shipment?.poNumber ? `PO ${shipment.poNumber}` : `load ${shipment?.id}`;

/** One shipment in the gate-placement shape, or null when the id no longer
 * resolves. Throws when the read itself fails. */
const fetchOne = async (shipmentId, outbound) => {
  const response = await API.graphql(
    graphqlOperation(
      outbound
        ? getOutboundShipmentGatePlacement
        : getInboundShipmentGatePlacement,
      { id: shipmentId },
    ),
  );
  const shipment = outbound
    ? response?.data?.getOutboundShipment
    : response?.data?.getInboundShipment;
  return shipment ? { shipment, outbound } : null;
};

/**
 * Every shipment a check-in controls, in the gate-placement shape (every
 * composite sort-key member, the placement stamps, state and end time). An
 * inbound row names one load; an outbound row names the load the driver
 * searched and, for an LTL "ship with" truck, every linked load in
 * `outboundIds` — one entry each, in that order. A row naming no shipment has
 * none, and so does an id that no longer resolves (it is left out rather than
 * reported as null). Shared by the two write-time guards — placement before
 * DOCKED, work done before DEPARTED — and by the manager Gate Queue, which
 * reads it for its docked rows.
 *
 * @returns {Promise<Array<{shipment: object, outbound: boolean}>>}
 */
export const fetchGateShipments = async (checkIn) => {
  const outbound = !checkIn?.inboundId;
  const ids = outbound
    ? groupCheckInShipmentIds(checkIn)
    : [checkIn.inboundId];
  if (!ids.length) return [];
  const found = await Promise.all(ids.map((id) => fetchOne(id, outbound)));
  return found.filter(Boolean);
};

/**
 * Moving a driver to DOCKED means the load is spotted, so the shipment's
 * placement stamp comes first. Run by saveGateCheckIn ahead of the DOCKED
 * write, for every surface that goes through it: a placement this refuses
 * stops the save, and the driver stays where the queue says they are. The
 * dispatch tabs additionally refuse the pick in their own select, since the
 * form already holds the shipment; this is the write-time guard behind them.
 *
 * Fetches first: the placement write has to echo every composite sort-key
 * member, and the check-in row carries none of them. No-ops when the shipment
 * is already placed (or the row names no shipment), so re-saving DOCKED and
 * the crew page's place-then-dock order are both safe.
 *
 * A linked truck (LTL "ship with") docks once for every load it carries, so
 * every member not yet placed is placed here — but only after every one of
 * them has passed the rule. Checking all before writing any keeps a refused
 * member from leaving its siblings placed under a driver who still reads
 * CALLED; the refusal names the load so the manager knows which one to fix.
 *
 * @returns {string|null} the placement timestamp written (the first one, when
 *   several loads were placed), or null if nothing needed placing
 * @throws {GatePlacementBlockedError} when a shipment is missing the
 *   carrier/trailer fields the crew Gate Queue also requires before placing, or
 *   (inbound) has no inventory to process
 */
export const stampPlacementFromGate = async (checkIn) => {
  const found = await fetchGateShipments(checkIn);
  const unplaced = found.filter(
    ({ shipment, outbound }) => !isAlreadyPlaced(shipment, outbound),
  );
  if (!unplaced.length) return null;

  // The one gate rule, shared with the crew Gate Queue's Set Placed button and
  // the dispatch tabs — DOCKED must not place a shipment they would refuse.
  // Every member is judged before any is written.
  const linked = found.length > 1;
  for (const { shipment, outbound } of unplaced) {
    const refusal = gatePlacementRefusal(shipment, outbound);
    if (refusal)
      throw new GatePlacementBlockedError(
        linked ? `${loadLabel(shipment)}: ${refusal}` : refusal,
      );
  }

  const stamps = [];
  for (const { shipment, outbound } of unplaced) {
    stamps.push(
      await placeShipment(shipment, outbound, {
        note: "Placed at dock from gate check-in",
      }),
    );
  }
  return stamps[0] ?? null;
};
